import { acceptHMRUpdate, defineStore } from "pinia";
import { dinero, add, multiply, toSnapshot, allocate } from "dinero.js";
import { GBP } from "@dinero.js/currencies";

import { getAnalytics, logEvent, setUserProperties } from "firebase/analytics";

export const useCartStore = defineStore("cart", {
  state: () => ({
    cartSessionId: "",
    preCartAlert: "",
    cartPayload: {},
    initTimestamp: new Date().toISOString(),
    cartItems: [],
    hasChanged: false,
    depositSchemeEnabled: false,
    depositSchemeSettings: {
      upfrontPaymentPercentage: 100,
      balanceDueBy: "2023-05-14T22:00:00.000Z",
    },
    showCartRecoveryModal: false,
    cartRecoveryModalTitle: "",
    cartRecoveryModalText: "",
    cartRecoverModalShowContinue: true,
    isRecoveredCart: false, // Indicates that the cart has been recovered from the backend
    checkoutSessionStarted: false,
    checkoutSessionStartedTimestamp: null,
    paymentSessionInitiated: false,
    paymentSessionInitiatedTimestamp: null,
    paymentSessionError: null,
    customerConsentPromotional: false,
    customerConsentEventUpdates: false,
    customerConsentTerms: false,
    enableRefundProtection: false,
    paymentSession: {},
    leadSource: "",
    moreCustomerInfo: {
      informationArray: [],
      informationString: "",
    },
    sourceOptions: [],
    customerEmail: "",
    customerEmailConfirm: "",
    emailMatchStatus: "incomplete",
    customerMobilePhone: "",
    customerFirstName: "",
    customerLastName: "",
    customerAddress: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      postcode: "",
      country: "",
    },
    initPaymentLoading: false,
    selectedUpsell: null,
    availableTicketTypes: null,
    payMethod: "",
    upsellOptions: [
      // TODO: Move these to the back-end
      {
        id: "terms-non-refundable",
        title: "Non-Refundable",
        shortDescription: "Tickets are non-refundable",
        productType: "ticket-terms",
        pricePercentage: 0,
        price: {
          amount: 0,
          currency: {
            code: "GBP",
            base: 10,
            exponent: 2,
          },
          scale: 2,
        },
        badges: [
          {
            text: "Standard",
            classes: [
              "inline-flex",
              " items-center",
              "px-2.5",
              "py-0.5",
              "rounded-full",
              "text-xs",
              "font-medium",
              "bg-gray-100",
              "text-fray-800",
            ],
          },
        ],
      },
      {
        id: "terms-refund-protection-a",
        title: "Add Refund Protection",
        shortDescription:
          "Protect your tickets in the event of cancellation or your inability to attend. Subject to Terms & Conditions.",
        productType: "ticket-terms",
        pricePercentage: 0.1,
        price: {
          amount: 1000,
          currency: {
            code: "GBP",
            base: 10,
            exponent: 2,
          },
          scale: 2,
        },
        badges: [
          {
            text: "Recommended",
            classes: [
              "inline-flex",
              " items-center",
              "px-2.5",
              "py-0.5",
              "rounded-full",
              "text-xs",
              "font-medium",
              "bg-yellow-100",
              "text-yellow-800",
            ],
          },
        ],
      },
    ],
    // BOX OFFICE CART STATE - This state is only used on the admin box office pages.
    boxOfficeCartEventSlug: "",
    boxOfficeOrderShowSubmitModal: false,
    boxOfficeOrderNotes: "",
    boxOfficeOrderProgress: "init",
    boxOfficeCartSubmitResult: {
      orderId: null,
      customerId: null,
      isNewCustomer: null,
      order: {},
      success: false,
    },
    boxOfficeCartSubmitAutoFulfill: true, // Automatically fulfil the order once submitted.
    showCartItemDetails: false, // Shows or hides item descriptions in cart
  }),

  getters: {
    allItems: (state) => state.cartItems,
    startTime: (state) => state.initTimestamp,
    totalItemSkus: (state) => state.cartItems.length,
    hasItems: (state) => state.cartItems.length > 0,
    totalTickets: (state) => {
      let total = 0;
      state.cartItems.forEach((item) => {
        total = total + item.qty;
      });
      return total;
    },
    totalCartSubtotal: (state) => {
      // let subtotal = dinero({ amount: 0, currency: GBP });
      const allPrices = [] as any;
      state.cartItems.forEach((item) => {
        const { price, qty } = item;
        const d = dinero(price);
        const total = multiply(d, qty || 0);
        allPrices.push(total);
      });
      if (!allPrices.length) {
        return 0;
      }
      const addMany = (addends: any) => addends.reduce(add);
      return toSnapshot(addMany(allPrices));
    },
    totalCartFees: (state) => {
      const allPrices = [] as any;
      state.cartItems.forEach((item) => {
        const { fee, qty } = item;
        const d = dinero(fee);
        const total = multiply(d, qty || 0);
        allPrices.push(total);
      });
      if (!allPrices.length) {
        return 0;
      }
      const addMany = (addends: any) => addends.reduce(add);
      // console.log(`${toUnit(addMany(allPrices), { digits: 2, round: down })}`)
      return toSnapshot(addMany(allPrices));
    },
    totalCartValue() {
      const totalPrice = dinero(
        this.totalCartSubtotal || { amount: 0, currency: GBP }
      );
      const totalFees = dinero(
        this.totalCartFees || { amount: 0, currency: GBP }
      );

      const total = add(totalPrice, totalFees);
      return toSnapshot(total);
    },
    depositSchemeInitialPayment() {
      const totalPrice = dinero(
        this.totalCartSubtotal || { amount: 0, currency: GBP }
      );
      const totalFees = dinero(
        this.totalCartFees || { amount: 0, currency: GBP }
      );
      const total = add(totalPrice, totalFees);
      const [initalPayment, remainingBalance] = allocate(total, [
        this.depositSchemeSettings.upfrontPaymentPercentage,
        100 - this.depositSchemeSettings.upfrontPaymentPercentage,
      ]);
      return toSnapshot(initalPayment);
    },
    depositSchemeRemainingBalance() {
      const totalPrice = dinero(
        this.totalCartSubtotal || { amount: 0, currency: GBP }
      );
      const totalFees = dinero(
        this.totalCartFees || { amount: 0, currency: GBP }
      );
      const total = add(totalPrice, totalFees);
      const [initalPayment, remainingBalance] = allocate(total, [
        this.depositSchemeSettings.upfrontPaymentPercentage,
        100 - this.depositSchemeSettings.upfrontPaymentPercentage,
      ]);
      return toSnapshot(remainingBalance);
    },
    availableUpsells() {
      // Calculate addon prices.
      // Addons can have a base price (defined in 'price') and a percentage (defined in 'pricePercentage') which will be added to the cart total.

      return this.upsellOptions.map((upsell) => {
        const { pricePercentage } = upsell;

        const addonBasePrice = dinero(upsell.price);
        const addonPercentageBasedPrice = multiply(
          dinero(this.totalCartValue),
          pricePercentage
        );
        const total = add(addonBasePrice, addonPercentageBasedPrice);
        return { ...upsell, price: toSnapshot(total) };
      });
    },
    cartSessionRequest: (state): any => {
      if (!state) {
        return null;
      }
      // TODO: Gather items in cart including refund protection
      // const items = [...state.cartItems]
      if (!state.paymentSessionInitiated) {
        return null;
      }
      return state.cartPayload;
    },
    checkoutFormValid: (state) => {
      // Check that the user has entered their email, first name and last name
      if (
        !state.customerEmail ||
        !state.customerFirstName ||
        !state.customerLastName
      ) {
        return false;
      }
      if (state.emailMatchStatus !== "match") {
        return false;
      }
      if (!state.customerConsentTerms) {
        return false;
      }
      return true;
    },
    cartItemQuantities: (state) => {
      if (!state.cartItems || !state.cartItems.length) {
        return {};
      }
      const itemsByKey = {};
      state.cartItems.forEach((i) => {
        itemsByKey[`${i.id}`] = i.qty;
      });
      return itemsByKey;
    },
    // headerStepsMap(state) {
    //     const { headerSteps } = state
    //     if (!headerSteps) return []
    //     return headerSteps.forEach(step => {
    //         if (state.currentStep) {
    //             return { ...step, status: 'current' }
    //         } else {
    //             return { ...step, status: 'upcoming' }
    //         }
    //     })
    // }
  },

  actions: {
    // Sets the cart session ID
    // TODO: Review setCartSessionId race condition fix.
    // This was set to async, but this could have been the cause for some race conditions
    setCartSessionId(c) {
      console.log(`Setting cart session ID to: ${c}`);
      this.cartSessionId = `${c}`;
      this.newCartId = `${c}`;
    },
    // Resets the cart
    resetCart() {
      // const config = useRuntimeConfig()
      // console.log(`Resetting cart`, config)
      // Clear any cartSessionId in localstorage
      // const cartSessionStorage = useStorage('wetix-cart-session-id', '')
      // console.log(`Local storage wetix-cart-session-id is NOT being used:`, cartSessionStorage)
      // cartSessionStorage.value = null
      // cartSessionStorage.value = null
      this.cartSessionId = "";
      // Reset pinia state
      this.$reset();
    },
    checkEmailMatchStatus() {
      console.log("Checking email match status");
      if (!this.customerEmail || !this.customerEmailConfirm) {
        return (this.emailMatchStatus = "incomplete");
      }
      if (this.customerEmail === this.customerEmailConfirm) {
        return (this.emailMatchStatus = "match");
      }
      return (this.emailMatchStatus = "mismatch");
    },
    // checkEmail: useDebounceFn(() => {
    //     // do something
    // }, 1000),
    addItemToCart(item: any, qty: any) {
      const { $getItemValueForAnalytics, $firebaseApp } = useNuxtApp();
      const analytics = getAnalytics($firebaseApp);

      console.log(`Adding ${qty} of item ${item.sku} to cart`);

      // Calculate the item total price

      // Fire analytics event
      const itemValueAnalytics = $getItemValueForAnalytics(item);
      logEvent(analytics, "add_to_cart", {
        parameters: {
          ...itemValueAnalytics,
          items: [
            {
              item_id: item.sku,
              item_name: item.name,
              currency: itemValueAnalytics.currency,
              price: itemValueAnalytics.value,
              quantity: qty,
              item_category: item.displayCategory,
              affiliation: "WeTix",
              item_brand: item.eventName,
            },
          ],
        },
      });

      // Check if item already in cart
      const foundItemIndex = useFindIndex(this.cartItems, { sku: item.sku });
      // console.log(`find found: ${foundItemIndex}`)
      // Add item to cart if it's not already there
      if (foundItemIndex === -1) {
        // console.log('Item not found already in cart -1, adding.')
        return this.cartItems.push({ ...item, qty });
      }
      // console.log('Checking if item removed..')
      // If qty changed to 0, remove from array
      if (qty === 0) {
        // console.log(`Item set to zero... removing from cart`)
        return (this.cartItems = useFilter(this.cartItems, { sku: item.sku }));
      }

      // console.log(`Item found already in cart at position ${foundItemIndex}, setting qty.`)
      this.cartItems[foundItemIndex].qty = qty;

      this.hasChanged = true;
    },
    removeItemFromCart(item) {
      const { $getItemValueForAnalytics, $firebaseApp } = useNuxtApp();
      const analytics = getAnalytics($firebaseApp);

      // Remove item from cart
      this.cartItems = useReject(this.cartItems, { sku: item.sku });

      const itemValueAnalytics = $getItemValueForAnalytics(item);
      logEvent(analytics, "add_to_cart", {
        parameters: {
          ...itemValueAnalytics,
          items: [
            {
              item_id: item.sku,
              item_name: item.name,
              currency: itemValueAnalytics.currency,
              price: itemValueAnalytics.value,
              item_category: item.displayCategory,
              affiliation: "WeTix",
              item_brand: item.eventName,
            },
          ],
        },
      });

      // console.log({ itemsAfter: this.cartItems.length })
    },
    async initPayment() {
      console.log("Initialising payment session");
      this.initPaymentLoading = true;
    },
    // Progress user from ticket selection to shopping cart (where customer information is collected)
    async proceedToCheckout(checkoutRoute) {
      console.log("Moving from cart to checkout");

      this.checkoutSessionStarted = true;
      this.checkoutSessionStartTime = new Date().toISOString();

      // Log analytics event
      const {
        $displayCurrencyForAnalytics,
        $getItemValueForAnalytics,
        $firebaseApp,
      } = useNuxtApp();
      const analytics = getAnalytics($firebaseApp);
      const analyticsCartTotalValue = $displayCurrencyForAnalytics(
        this.totalCartValue
      );
      const analyticsCartItems = this.cartItems.map((item) => {
        const itemValueAnalytics = $getItemValueForAnalytics(item);
        return {
          item_id: item.sku,
          item_name: item.name,
          currency: itemValueAnalytics.currency,
          price: itemValueAnalytics.value,
          quantity: item.qty,
          item_category: item.displayCategory,
          affiliation: "WeTix",
          item_brand: item.eventName,
        };
      });
      logEvent(analytics, "begin_checkout", {
        parameters: { ...analyticsCartTotalValue, items: analyticsCartItems },
      });
      // TODO: Fire analytics event checkout_progress when user starts completing their details.
      // TODO: Save cart to session storage

      // Proceed to checkout route
      // Note: Ensure to always await on navigateTo
      return navigateTo({
        path: checkoutRoute,
        query: { cartSessionId: this.cartSessionId },
      });
    },
    // Prepare state for proceeding to checkout, and fire analytics events.
    async prepareProceedToPayment({ tenantId, eventSlug, returnUrlPath }) {
      console.log("Preparing to initiate a payment session", {
        tenantId,
        eventSlug,
        returnUrlPath,
      });
      try {
        // TODO: Validate checkout data
        // TODO: Save cart to session storage

        // if (this.paymentSessionInitiated) return console.error(`Payment session already initiated`)
        this.paymentSessionInitiated = true;
        this.paymentSessionInitiatedTimestamp = new Date().toISOString();

        // Analytics
        const { $firebaseApp } = useNuxtApp();
        const analytics = getAnalytics($firebaseApp);
        // Log analytics event - checkout progressed to step 3 (payment)
        logEvent(analytics, "checkout_progress", {
          parameters: {
            checkout_step: 2,
            items: { ...this.cartItems },
          },
        });
        // Log analytics event - checkout progressing to stripe checkout
        logEvent(analytics, "set_checkout_option", {
          parameters: {
            items: {
              checkout_step: 3,
              checkout_option: "payment_method",
              value: "stripe_checkout",
            },
          },
        });

        // Construct paymentPlan payload if deposit scheme is selected payment method
        const cartIsPaymentPlan = this.payMethod === "deposit-scheme"; // When we add other payment plan methods, we'll need to update this.
        let paymentPlanPayload = null;
        if (cartIsPaymentPlan) {
          paymentPlanPayload = {
            paymentPlanType: this.payMethod,
            paymentPlanTypeId: "deposit-scheme-v1",
            paymentPlanDislayName: "Deposit Scheme",
            paymentPlanData: {
              // The type of this object will change depending on the payment plan type
              depositSchemePaymentStatus: "initial-payment-unpaid",
              depositSchemeInitialPayment: this.depositSchemeInitialPayment,
              depositSchemeRemainingBalance: this.depositSchemeRemainingBalance,
              depositSchemePaymentDeadline:
                this.depositSchemeSettings.balanceDueBy,
              depositSchemeSettingsSnapshot: this.depositSchemeSettings,
              depositSchemeEnabledSnapshot: this.depositSchemeEnabled,
            },
          };
        }

        // Construct payload to post to stripe checkout initialiser
        const cartPayload = {
          cartSessionId: this.cartSessionId,
          tenantId,
          eventSlug,
          cartItems: this.cartItems,
          customerData: {
            name: `${this.customerFirstName} ${this.customerLastName}`,
            firstName: this.customerFirstName,
            lastName: this.customerLastName,
            address: this.customerAddress,
            email: this.customerEmail,
            phone: this.customerMobilePhone,
          },
          cartData: {
            totalCartValue: this.totalCartValue,
            totalCartSubtotal: this.totalCartSubtotal,
            totalCartFees: this.totalCartFees,
            selectedUpsell: this.selectedUpsell,
            frontendCartInitTimestamp: this.initTimestamp,
            payMethod: this.payMethod,
            cartIsPaymentPlan,
            cartPaymentPlanSuffix: cartIsPaymentPlan ? "Deposit Payment" : "",
            paymentPlanPayload,
          },
          returnUrlBase: `${window.location.origin}${returnUrlPath}`,
        };

        this.cartPayload = cartPayload;
        console.log(
          "Stripe checkout session request payload: ",
          unref(cartPayload)
        );

        // Code flow now returns to checkout page where a new session is initiated with the backend, and state patched.
        return true;
      } catch (error) {
        console.error("Error creating payment session:", error);
        this.paymentSessionInitiated = false;
        return false;
      }
    },
    async onCheckoutSuccess() {
      // Todo: Update back-end state to indicate the cartSessionId is expired/unrecoverable
      // TODO: Analytics logging.
      return console.log("onCheckoutSuccess not implemented yet");
    },
    getItemSubtotal(item) {
      const { price, fee, qty } = item;
      const p = dinero(price);
      const f = dinero(fee);
      const ptTotal = add(p, f);
      return toSnapshot(multiply(ptTotal, qty || 0));
    },
    async applyZeroPriceToAllItems() {
      const newItems = [];
      this.cartItems.forEach((item) => {
        const i = {
          ...item,
        };
        i.price.amount = 0;
        i.fee.amount = 0;
        newItems.push(i);
      });
      this.cartItems = [...newItems];
    },
    // async boxOfficeSaveCartSession({ tenantId, alert }) {
    //     const { $notify } = useNuxtApp()
    //     const config = useRuntimeConfig();
    //     if (!this.cartSessionId) {
    //         console.error(`No cart session ID found.`)
    //         $notify({ type: 'error', title: 'No cart session ID found.' })
    //         return
    //     }
    //     try {
    //         // Post the whole client cart state to the backend for storage / recovery
    //         await $fetch(`${config.public.WETIX_SERVICE_API_BASE_URL}/v1/cart/${tenantId}/session/${this.cartSessionId}/update`,
    //             {
    //                 method: 'POST',
    //                 body: { cartState: this.$state }
    //             })
    //         if (!alert) return
    //         $notify({ title: 'Saved', type: 'success', message: 'Cart session saved.' })
    //     } catch (error) {
    //         $notify({ title: 'Error Saving Cart', type: 'error', message: error.message || null })
    //         console.error(`Failed to post the cart state on backend`)
    //         throw new Error(error)
    //     }
    // },
    // async boxOfficeSubmitOrder({ tenantId }) {
    //     console.log(`Submitting box office order`)
    //     const { $notify } = useNuxtApp()
    //     const authState = useAuthState()
    //     const config = useRuntimeConfig();
    //     try {

    //         if (!this.boxOfficeCartEventSlug) {
    //             return $notify({ type: 'error', title: 'No event slug found.' })
    //         }

    //         // If payMethod is stripe-checkout, do not allow submitting via this function.
    //         if (this.payMethod === 'stripe-checkout') {
    //             throw new Error('Cannot submit box office order using stripe checkout.')
    //         }

    //         // If payMethod is comp-free, zero all prices.
    //         if (this.payMethod === 'comp-free') {
    //             await this.applyZeroPriceToAllItems()
    //         }

    //         // Gather data on the user submitting the order.
    //         const userData = {
    //             displayName: authState.currentUser?.displayName,
    //             email: authState.currentUser?.email,
    //             uid: authState.currentUser?.uid,
    //         }

    //         // Construct cart payload to submit to back-end
    //         const cartPayload = {
    //             isBoxOfficeOrder: true,
    //             boxOfficeOrderNotes: this.boxOfficeOrderNotes,
    //             boxOfficeOrderCreatedByUser: { ...userData },
    //             cartSessionId: this.cartSessionId,
    //             tenantId: tenantId,
    //             eventSlug: this.boxOfficeCartEventSlug, // Get eventSlug from boxOfficeCartEventSlug
    //             cartItems: this.cartItems,
    //             customerData: {
    //                 name: `${this.customerFirstName} ${this.customerLastName}`,
    //                 firstName: this.customerFirstName,
    //                 lastName: this.customerLastName,
    //                 address: this.customerAddress,
    //                 email: this.customerEmail,
    //                 phone: this.customerMobilePhone
    //             },
    //             cartData: {
    //                 totalCartValue: this.totalCartValue,
    //                 totalCartSubtotal: this.totalCartSubtotal,
    //                 totalCartFees: this.totalCartFees,
    //                 selectedUpsell: this.selectedUpsell,
    //                 frontendCartInitTimestamp: this.initTimestamp,
    //                 boxOfficeAdminOrderNotes: this.boxOfficeAdminOrderNotes,
    //                 payMethod: this.payMethod,
    //                 boxOfficeOrderSubmitDateFrontend: new Date().toISOString(),
    //             },
    //         }
    //         this.cartPayload = cartPayload

    //         // Set status to 'submitting'
    //         this.boxOfficeOrderProgress = 'submitting'

    //         // Save the cart session.
    //         await this.boxOfficeSaveCartSession({ tenantId, alert: false })

    //         // Post order to back-end
    //         const response = await wetixFetch(`/v1/boxoffice/${tenantId}/order/create-manual`, {
    //             method: 'POST',
    //             body: {
    //                 ...cartPayload,
    //                 autoFulfill: this.boxOfficeCartSubmitAutoFulfill // Automatically fulfil the order.
    //             }
    //         }).catch((error) => {
    //             console.error(`Fetch error:`, error.data)
    //             return $notify({ type: 'error', title: 'Error', message: error.data })
    //         }) as any

    //         console.log(`Box office create order response: `, response)

    //         if (response?.success) {
    //             $notify({ type: 'success', title: 'Order Submitted', message: `Order with ID ${response.orderId} is now processing.` })
    //             this.boxOfficeOrderProgress = 'created'
    //             this.boxOfficeCartSubmitResult = { ...response }
    //         } else {
    //             console.error(`Something went wrong during store/cart/boxOfficeSubmitOrder`)
    //             // $notify({ type: 'error', title: 'Error', message: `Something went wrong.` })
    //             this.boxOfficeOrderProgress = 'error'
    //         }

    //         return true
    //     } catch (error) {
    //         console.error(`Error submitting order:`, error)
    //         this.boxOfficeOrderProgress = 'error'
    //         throw new Error(error)
    //     }
    // }
    // Fetch an array of source options
    async fetchLeadSourceOptions(tenantId) {
      const config = useRuntimeConfig();
      try {
        this.sourceOptions = await $fetch(
          `${config.public.WETIX_SERVICE_API_BASE_URL}/v1/cart/${tenantId}/get-lead-source-options`,
          {
            method: "GET",
          }
        );
        console.log("Got lead source options:", this.sourceOptions);
      } catch (error) {
        console.error("Failed to fetch lead source options");
        throw new Error(error);
      }
    },
    // Set the lead source option
    setLeadSource(source) {
      this.leadSource = source;
    },

    // More customer info functions
    addToMoreCustomerInfoInformationArray(i) {
      this.moreCustomerInfo.informationArray.push(i);
    },
    addToMoreCustomerInfoInformationString(i) {
      this.moreCustomerInfo.informationString = i;
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCartStore, import.meta.hot));
}
